/**
 * Wallet & Referrals (Phase 5 / Module 3) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/wallet.ts`. Used by both the
 * API route handlers (input validation) and downstream type sharing.
 *
 * Conventions:
 *   - All money amounts are integers in **paise** (1 INR = 100 paise).
 *   - Sign conventions live in the application layer: credits are positive,
 *     debits negative. The DB does not enforce sign so `admin_adjustment`
 *     can go either way.
 *   - Academic year strings are validated for shape only (`YYYY-YYYY`); the
 *     business logic that picks the *current* year lives in
 *     `apps/api/src/lib/wallet.ts` (`currentAcademicYear`).
 *
 * Spec: docs/deep-research-report4.md §"Wallet and Referral Ledger".
 */
import { z } from 'zod';
import { idSchema, paginationSchema, turnstileTokenSchema } from './common.js';

// ----- Common enums / scalars ---------------------------------------------

export const referralOwnerTypeSchema = z.enum(['teacher', 'student']);
export type ReferralOwnerType = z.infer<typeof referralOwnerTypeSchema>;

export const referralCodeStatusSchema = z.enum([
  'active',
  'revoked',
  'replaced',
]);

export const referralEventStatusSchema = z.enum([
  'pending',
  'confirmed',
  'rejected',
  'clawed_back',
]);

export const referralSourceSchema = z.enum([
  'admissions_form',
  'admin_enrollment',
  'activation',
]);

export const walletEntryTypeSchema = z.enum([
  'referral_credit',
  'withdrawal_debit',
  'admin_adjustment',
  'clawback_debit',
]);
export type WalletEntryType = z.infer<typeof walletEntryTypeSchema>;

export const withdrawalMethodSchema = z.enum(['upi_bank', 'fee_credit', 'cash']);
export type WithdrawalMethod = z.infer<typeof withdrawalMethodSchema>;

export const withdrawalStatusSchema = z.enum([
  'requested',
  'approved',
  'rejected',
  'paid',
  'cancelled',
]);

/**
 * Academic-year string shape. Format: `YYYY-YYYY` (e.g. `2026-2027`).
 * The second year must be exactly one greater than the first; this is
 * enforced via `.refine` so we can return a clean validation error.
 */
export const academicYearSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{4}$/u, 'Academic year must be in YYYY-YYYY format')
  .refine(
    (value) => {
      const parts = value.split('-').map((n) => Number.parseInt(n, 10));
      const a = parts[0];
      const b = parts[1];
      return (
        a !== undefined &&
        b !== undefined &&
        Number.isFinite(a) &&
        Number.isFinite(b) &&
        b === a + 1
      );
    },
    { message: 'Academic year second value must be first + 1' },
  );

/**
 * The shareable referral code. Crockford Base32 (no I, L, O, U) with the
 * fixed `TCH-BBL-` or `STU-BBL-` prefix and a 6-character body.
 *
 * Letters in the body are uppercased on parse so users typing in lowercase
 * still get a hit.
 */
export const referralCodePatternSchema = z
  .string()
  .trim()
  .transform((value) => value.toUpperCase())
  .pipe(
    z
      .string()
      .regex(
        /^(?:TCH|STU)-BBL-[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{6}$/u,
        'Invalid referral code format',
      ),
  );

const reasonSchema = z.string().trim().min(2).max(1000);
const longReasonSchema = z.string().trim().min(20).max(2000);
const notesSchema = z.string().trim().min(1).max(2000);

// ----- /api/referrals/validate (public) -----------------------------------

export const referralValidateRequestSchema = z
  .object({
    code: referralCodePatternSchema,
    turnstileToken: turnstileTokenSchema,
  })
  .strict();
export type ReferralValidateRequest = z.infer<typeof referralValidateRequestSchema>;

export const referralValidateResponseSchema = z.discriminatedUnion('valid', [
  z
    .object({
      valid: z.literal(true),
      ownerType: referralOwnerTypeSchema,
      ownerDisplayName: z.string(),
    })
    .strict(),
  z
    .object({
      valid: z.literal(false),
      // SECURITY: the public validate endpoint MUST NOT distinguish between
      // a code that never existed, one revoked for abuse, and one that was
      // rotated/replaced. All inactive states collapse to a single opaque
      // `invalid` reason so an unauthenticated caller learns nothing about a
      // code's lifecycle. The lifecycle distinction is preserved internally
      // (see lib/referral.ts CodeLookupResult) for the authenticated
      // attribution path only.
      reason: z.literal('invalid'),
    })
    .strict(),
]);

// ----- /api/referrals/my-code ---------------------------------------------

/**
 * Owners (teacher, student) fetch their own code; parents fetch on behalf
 * of a child via `?studentId=<id>` query param.
 */
export const referralMyCodeQuerySchema = z
  .object({
    studentId: idSchema.optional(),
  })
  .strict();

export const referralRegenerateRequestSchema = z
  .object({
    studentId: idSchema.optional(),
    reason: reasonSchema,
  })
  .strict();

// ----- /api/referrals/mine ------------------------------------------------

export const referralMineQuerySchema = paginationSchema.extend({
  studentId: idSchema.optional(),
  status: referralEventStatusSchema.optional(),
  academicYear: academicYearSchema.optional(),
});

// ----- /api/wallet --------------------------------------------------------

export const walletSummaryQuerySchema = z
  .object({
    studentId: idSchema.optional(),
    academicYear: academicYearSchema.optional(),
  })
  .strict();

export const walletLedgerQuerySchema = paginationSchema.extend({
  studentId: idSchema.optional(),
  entryType: walletEntryTypeSchema.optional(),
  academicYear: academicYearSchema.optional(),
});

// ----- /api/wallet/withdrawals --------------------------------------------

const upiBankDestinationSchema = z
  .object({
    upiId: z
      .string()
      .trim()
      .min(3)
      .max(120)
      .regex(/^[\w.-]+@[\w.-]+$/u, 'Enter a valid UPI handle')
      .optional(),
    ifsc: z
      .string()
      .trim()
      .max(20)
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/u, 'Enter a valid IFSC')
      .optional(),
    accountNumber: z
      .string()
      .trim()
      .min(6)
      .max(40)
      .regex(/^[A-Za-z0-9]+$/u, 'Account number must be alphanumeric')
      .optional(),
    accountName: z.string().trim().min(2).max(160).optional(),
  })
  .strict()
  .refine(
    (d) =>
      Boolean(d.upiId) ||
      (Boolean(d.ifsc) && Boolean(d.accountNumber) && Boolean(d.accountName)),
    {
      message:
        'Provide either upiId, or all of ifsc + accountNumber + accountName',
    },
  );

const feeCreditDestinationSchema = z
  .object({
    targetStudentId: idSchema,
  })
  .strict();

export const withdrawalRequestCreateSchema = z
  .discriminatedUnion('method', [
    z
      .object({
        method: z.literal('upi_bank'),
        amountPaise: z.number().int().positive().max(10_000_000),
        studentId: idSchema.optional(),
        destination: upiBankDestinationSchema,
        notes: notesSchema.optional(),
      })
      .strict(),
    z
      .object({
        method: z.literal('fee_credit'),
        amountPaise: z.number().int().positive().max(10_000_000),
        studentId: idSchema.optional(),
        destination: feeCreditDestinationSchema,
        notes: notesSchema.optional(),
      })
      .strict(),
    z
      .object({
        method: z.literal('cash'),
        amountPaise: z.number().int().positive().max(10_000_000),
        studentId: idSchema.optional(),
        destination: z.null().default(null),
        notes: notesSchema.optional(),
      })
      .strict(),
  ]);

export type WithdrawalRequestCreateInput = z.infer<
  typeof withdrawalRequestCreateSchema
>;

export const withdrawalListQuerySchema = paginationSchema.extend({
  status: withdrawalStatusSchema.optional(),
  academicYear: academicYearSchema.optional(),
  ownerType: referralOwnerTypeSchema.optional(),
});

export const withdrawalRejectSchema = z
  .object({
    reason: reasonSchema,
  })
  .strict();

export const withdrawalApproveSchema = z
  .object({
    notes: notesSchema.optional(),
  })
  .strict();

export const withdrawalMarkPaidSchema = z
  .object({
    notes: notesSchema.optional(),
    paidReference: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

// ----- /api/wallet/adjustments (super_admin) ------------------------------

export const walletAdjustmentSchema = z
  .object({
    ownerType: referralOwnerTypeSchema,
    ownerRefId: idSchema,
    academicYear: academicYearSchema,
    /** Signed amount in paise. Positive = credit, negative = debit. */
    amountPaise: z.number().int().refine((n) => n !== 0, {
      message: 'amountPaise must be non-zero',
    }),
    entryType: walletEntryTypeSchema.default('admin_adjustment'),
    notes: longReasonSchema,
  })
  .strict();

// ----- Response shapes ----------------------------------------------------

export const referralCodeResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  ownerType: referralOwnerTypeSchema,
  ownerRefId: z.string(),
  status: referralCodeStatusSchema,
  createdAt: z.string(),
  shareUrl: z.string(),
});

export const referralEventResponseSchema = z.object({
  id: z.string(),
  referralCodeId: z.string(),
  referrerType: referralOwnerTypeSchema,
  referrerRefId: z.string(),
  // PII minimisation: we deliberately omit the internal student UUID
  // (`referredStudentId`) — the referral-event `id` above is the stable
  // reference the portal uses. The name is truncated to "Firstname S." by
  // the API before it ever leaves the server.
  referredStudentName: z.string(),
  academicYear: z.string(),
  status: referralEventStatusSchema,
  source: referralSourceSchema,
  enteredAt: z.string(),
  enrolledAt: z.string().nullable(),
  firstFeePaidAt: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
});

export const walletSummaryResponseSchema = z.object({
  academicYear: z.string(),
  ownerType: referralOwnerTypeSchema,
  ownerRefId: z.string(),
  balancePaise: z.number().int(),
  lifetimeCreditedPaise: z.number().int(),
  lifetimeDebitedPaise: z.number().int(),
  capStatus: z.object({
    confirmedCount: z.number().int().min(0),
    totalCreditedPaise: z.number().int().min(0),
    maxRewardsPaise: z.number().int().min(0),
    maxReferralsCount: z.number().int().min(0),
    capReached: z.boolean(),
  }),
  nextWithdrawalWindow: z.object({
    opensAt: z.string(),
    closesAt: z.string(),
    isOpen: z.boolean(),
  }),
});

export const walletLedgerEntryResponseSchema = z.object({
  id: z.string(),
  ownerType: referralOwnerTypeSchema,
  ownerRefId: z.string(),
  academicYear: z.string(),
  entryType: walletEntryTypeSchema,
  amountPaise: z.number().int(),
  currency: z.string(),
  notes: z.string().nullable(),
  referralEventId: z.string().nullable(),
  withdrawalRequestId: z.string().nullable(),
  createdAt: z.string(),
});

export const withdrawalRequestResponseSchema = z.object({
  id: z.string(),
  ownerType: referralOwnerTypeSchema,
  ownerRefId: z.string(),
  academicYear: z.string(),
  requestedAmountPaise: z.number().int(),
  method: withdrawalMethodSchema,
  destination: z.unknown().nullable(),
  status: withdrawalStatusSchema,
  requestedAt: z.string(),
  reviewedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  notes: z.string().nullable(),
  cooldownUntil: z.string().nullable(),
});

// ----- Type exports -------------------------------------------------------

export type ReferralValidateResponse = z.infer<
  typeof referralValidateResponseSchema
>;
export type ReferralCodeResponse = z.infer<typeof referralCodeResponseSchema>;
export type ReferralEventResponse = z.infer<typeof referralEventResponseSchema>;
export type WalletSummaryResponse = z.infer<typeof walletSummaryResponseSchema>;
export type WalletLedgerEntryResponse = z.infer<
  typeof walletLedgerEntryResponseSchema
>;
export type WithdrawalRequestResponse = z.infer<
  typeof withdrawalRequestResponseSchema
>;
export type WalletAdjustmentInput = z.infer<typeof walletAdjustmentSchema>;
